/**
 * sd-gooey-text.js
 *
 * Morphing headline for the Savor theme.
 * Faithful port of the 21st.dev GooeyText component:
 *   two overlapping <span>s cross-blur between phrases under an SVG threshold
 *   (feColorMatrix alpha-threshold) filter, driven by requestAnimationFrame.
 *
 * Pure JS → runs in Chrome, Safari, Firefox, Edge.
 * Reduced motion → shows the first phrase static; no blur, no rAF loop.
 *
 * Data-attributes (from section settings):
 *   data-morph-time   — seconds per morph transition (default 1)
 *   data-cooldown     — seconds each phrase is held fully visible (default 0.5)
 *
 * Required refs (in the Liquid markup):
 *   ref="text1"  — first overlapping span
 *   ref="text2"  — second overlapping span
 *
 * Phrases are read from [data-phrase] children inside the component element.
 */

import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

class SdGooeyText extends Component {
  requiredRefs = ['text1', 'text2'];
  #raf = null;

  connectedCallback() {
    super.connectedCallback();

    const phrases = Array.from(this.querySelectorAll('[data-phrase]'))
      .map((el) => el.textContent.trim())
      .filter(Boolean);

    const { text1, text2 } = this.refs;
    if (phrases.length === 0) return;

    // Single phrase or reduced-motion: show phrase[0] static, no animation.
    if (phrases.length === 1 || prefersReducedMotion()) {
      text1.textContent = phrases[0];
      text1.style.opacity = '100%';
      text2.style.opacity = '0%';
      return;
    }

    const morphTime    = Math.max(0.1, Number(this.dataset.morphTime) || 1);
    const cooldownTime = Math.max(0,   Number(this.dataset.cooldown)  || 0.5);

    let textIndex = phrases.length - 1;
    let time      = Date.now();
    let morph     = 0;
    let cooldown  = cooldownTime;

    // Seed both spans so the first morph has a visible "from".
    text1.textContent = phrases[textIndex % phrases.length];
    text2.textContent = phrases[(textIndex + 1) % phrases.length];

    const setMorph = (fraction) => {
      text2.style.filter  = `blur(${Math.min(8 / fraction - 8, 100)}px)`;
      text2.style.opacity = `${Math.pow(fraction, 0.4) * 100}%`;
      const inv = 1 - fraction;
      text1.style.filter  = `blur(${Math.min(8 / inv - 8, 100)}px)`;
      text1.style.opacity = `${Math.pow(inv, 0.4) * 100}%`;
    };

    const doCooldown = () => {
      morph              = 0;
      text2.style.filter  = '';
      text2.style.opacity = '100%';
      text1.style.filter  = '';
      text1.style.opacity = '0%';
    };

    const doMorph = () => {
      morph    -= cooldown;
      cooldown  = 0;
      let fraction = morph / morphTime;
      if (fraction > 1) {
        cooldown = cooldownTime;
        fraction = 1;
      }
      setMorph(fraction);
    };

    const animate = () => {
      this.#raf    = requestAnimationFrame(animate);
      const now    = Date.now();
      const dt     = (now - time) / 1000;
      time         = now;
      const wasCooling = cooldown > 0;
      cooldown -= dt;

      if (cooldown <= 0) {
        if (wasCooling) {
          // Advance to the next phrase pair.
          textIndex++;
          text1.textContent = phrases[textIndex % phrases.length];
          text2.textContent = phrases[(textIndex + 1) % phrases.length];
        }
        doMorph();
      } else {
        doCooldown();
      }
    };

    animate();
  }

  /** Cancel the rAF loop if the section is removed (e.g. editor drag-delete). */
  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#raf !== null) {
      cancelAnimationFrame(this.#raf);
      this.#raf = null;
    }
  }
}

if (!customElements.get('sd-gooey-text')) {
  customElements.define('sd-gooey-text', SdGooeyText);
}
